import React, { FC, useEffect, useRef, useState } from 'react'
import * as yup from 'yup'
import { Formik, FormikValues } from 'formik'
import styles from './CreateClaim.module.css'
import CustomInput from '../UI/CustomInput/CustomInput'
import CustomButton from '../UI/CustomButton/CustomButton'
import CustomTextArea from '../UI/CustomTextArea/CustomTextArea'
import { useNavigate, useParams } from 'react-router-dom'
import PATH from '../../navigation/path'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { waitTimer } from '../../utils/waitTimer'
import s from '../../styles/common/Form.module.css'
import { format } from 'date-fns'
import { clearError } from '../../redux/reducers/claims/claimsSlice'
import DropDown from '../DropDown/DropDown'
import { Calendar } from '../CustomCalendar/components'
import DatePicker from 'react-datepicker'
import {
  createClaim,
  getAllClaimsFromPlatforms,
  getClaimComments,
  getCurrentClaim,
} from '../../redux/actions/claimsAction'
import { getPlaces } from '../../redux/actions/placesAction'
import { ClientsType, ManagerType } from '../../redux/actions/types'
import CreateClientModal from '../CreateClientModal/CreateClientModal'
import CreateClientManagerModal from '../CreateClientManagerModal/CreateClientManagerModal'
import { discountType } from '../../constants/constants'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import ModalHeader from '../ModalHeader/ModalHeader'
import plusIcon from '../../assets/icons/plusIcon.svg'
import { useMediaQuery } from 'react-responsive'
import { useToast } from '../../context/ToastContext'

export type CreateClaimPropsType = {
  onClickHandler: () => void
  startDate?: Date
  platformName?: string | string[]
  customer?: string
  id?: number
  size?: number
  client?: ClientsType[]
  clientId?: any
  managerId?: number | string
  isWaitingList?: boolean
}

const CreateClaim: FC<CreateClaimPropsType> = ({
  onClickHandler,
  startDate,
  platformName,
  customer,
  id,
  size,
  client,
  clientId,
  managerId,
  isWaitingList,
}) => {
  const [openStartDate, setOpenStartDate] = useState(false)
  const [openFinishDate, setOpenFinishDate] = useState(false)
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const [openStartTime, setOpenStartTime] = useState(false)
  const [openFinishTime, setOpenFinishTime] = useState(false)

  const [startDateString, setStartDateString] = useState('')
  const [finishDateString, setFinishDateString] = useState('')

  const [timeStart, setTimeStart] = useState<Date | null>(null)
  const [timeFinish, setTimeFinish] = useState<Date | null>(null)

  const [clientType, setClientType] = useState<'individual' | 'legal'>('individual')
  const [selectedClient, setSelectedClient] = useState<any>('')

  const [clientManagers, setClientManagers] = useState<ManagerType[]>([])

  const [selectedStartDate, setSelectedStartDate] = React.useState(new Date())
  const [selectedFinishDate, setSelectedFinishDate] = React.useState(new Date())
  const [platformSize, setPlatformSize] = useState<number | number[]>(-1)
  const [platformNames, setPlatformNames] = useState<{ id: number; name: string }[]>([])
  const [platformId, setPlatformId] = useState<number>(-1)
  const [isRetrying, setIsRetrying] = useState<boolean>(false)
  const [err, setErr] = useState(null)
  const [isCreateClient, setIsCreateClient] = useState(false)
  const [isCreateManager, setIsCreateManager] = useState(false)

  const { places } = useAppSelector((state) => state.places)

  const resetFormState = () => {
    setOpenStartDate(false)
    setOpenFinishDate(false)
    setOpenStartTime(false)
    setOpenFinishTime(false)
    setStartDateString('')
    setFinishDateString('')
    setTimeStart(null)
    setTimeFinish(null)
    setClientType('individual')
    setSelectedClient(undefined)
    setClientManagers([])
    setSelectedStartDate(new Date())
    setSelectedFinishDate(new Date())
    setPlatformSize(-1)
    setPlatformNames([])
    setPlatformId(-1)
    setIsRetrying(false)
    setErr(null)
    setIsCreateClient(false)
    setIsCreateManager(false)
    setValidateError('')
    setClaimError(false)

    if (formikRef.current) {
      formikRef.current()
    }
  }

  const handleCloseModal = () => {
    resetFormState()
    onClickHandler()
  }

  const formikRef = useRef<(() => void) | null>(null)
  const [validateError, setValidateError] = useState('')
  const [claimError, setClaimError] = useState<boolean>(false)
  const { claimId } = useParams<{ claimId: string }>()
  const { token, user } = useAppSelector((state) => state.auth)
  const { error } = useAppSelector((state) => state.claims)

  const { showSuccess, showError } = useToast()

  const filteredClients = client?.filter((client) => client.type === clientType)

  useEffect(() => {
    if (selectedClient) {
      const filteredClient = client?.find((client) => client.id == selectedClient)

      if (filteredClient) {
        setClientManagers(filteredClient?.managersData)
      } else {
        setClientManagers([])
      }
    }
  }, [selectedClient, client])

  useEffect(() => {
    if (filteredClients?.length === 1) {
      setSelectedClient(filteredClients[0]?.id) // Устанавливаем первое значение в selectedClient
    }
  }, [filteredClients])

  const getFormatDate = (time: Date | null) => {
    if (time !== null) {
      const hours: string = time.getHours().toString().padStart(2, '0')
      const minutes: string = time.getMinutes().toString().padStart(2, '0')
      const seconds: string = time.getSeconds().toString().padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    } else {
      return null
    }
  }

  const onChangeStartDate = (date: Date) => {
    setSelectedStartDate(date)
    setStartDateString(format(date, 'dd.MM.yyyy'))
    setOpenStartDate(false)
  }

  useEffect(() => {
    if (startDate) {
      onChangeStartDate(startDate)
      onChangeFinishDate(startDate)
    }
  }, [startDate])

  const onChangeFinishDate = (date: Date) => {
    setSelectedFinishDate(date)
    setFinishDateString(format(date, 'dd.MM.yyyy'))
    setOpenFinishDate(false)
  }

  const navigate = useNavigate()

  const customInput = <input type='text' placeholder='Когда?' className={styles.dateValue} />

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (token && (!places?.places || places.places.length === 0)) {
      dispatch(
        getPlaces({
          token,
          page: 1,
          itemsPerPage: 1000,
        }),
      )
    }
  }, [token, dispatch, places?.places])

  const validationSchema = yup.object().shape({
    guestsNumber: yup
      .number()
      .typeError('Введите количество в числовом значении')
      .integer('Количество должно быть целым числом')
      .positive('Количество должно быть положительным')
      .required('Поле не может быть пустым')
      .test('is-not-zero', 'Количество не должно быть равно нулю', (value) => value !== 0)
      .test(
        'max-capacity',
        `Количество гостей не может превышать ${platformSize}`,
        (value) => value !== undefined && value <= +platformSize,
      ),
  })

  const formatToYearMonth = (dateString: string) => {
    if (!dateString) return null
    const parts = dateString.split('.')
    if (parts.length === 3) {
      const day = parts[0]
      const month = parts[1]
      const year = parts[2]
      return `${year}-${month}`
    }
    return null
  }

  const yearMonthFormat = formatToYearMonth(startDateString)

  const handleSubmitHandler = async (values: FormikValues, e: any) => {
    // e.preventDefault();

    if (startDateString === '') {
      setValidateError('Дата начала мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      waitTimer(() => clearError())
      return
    }

    if (finishDateString === '') {
      setValidateError('Дата окончания мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      waitTimer(() => clearError())
      return
    }

    if (timeStart === null) {
      setValidateError('Время начала мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      return
    }

    if (timeFinish === null) {
      setValidateError('Время окончания мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      return
    }

    const startDateObj = new Date(selectedStartDate)
    const finishDateObj = new Date(selectedFinishDate)
    const oneMonthFromStart = new Date(startDateObj)
    oneMonthFromStart.setMonth(oneMonthFromStart.getMonth() + 1)

    if (finishDateObj > oneMonthFromStart) {
      setValidateError('Длительность заявки не может превышать 1 месяц')
      waitTimer(() => setValidateError(''))
      return
    }

    const startDate = `${startDateString.split('.').reverse().join('-')} ${getFormatDate(
      timeStart,
    )}`
    const endDate = `${finishDateString.split('.').reverse().join('-')} ${getFormatDate(
      timeFinish,
    )}`

    const resultAction = await dispatch(
      createClaim({
        token,
        userId: String(user.id),
        peopleCount: values.guestsNumber,
        placeId: id ? String(id) : String(platformId),
        startDate: startDate,
        endDate: endDate,
        client: selectedClient,
        manager: values?.managerId,
        comment: values.comment || '',
      }),
    )

    if (createClaim.rejected.match(resultAction)) {
      const errorMessage: any = resultAction.payload || resultAction.error.message

      if (errorMessage.includes('уже забронирована в периоде')) {
        setErr(errorMessage)
        setIsRetrying(true)
      }
      if (!errorMessage.includes('уже забронирована в периоде')) {
        showError(errorMessage)
      }
    } else {
      showSuccess('Заявка успешно создана')
      navigate(`${PATH.claimCardPage}${resultAction.payload.id}`)
      dispatch(
        getAllClaimsFromPlatforms({
          companyId: user?.employee?.company?.id ? String(user?.employee?.company?.id) : undefined,
          yearsAndMonth: yearMonthFormat ?? undefined,
          token,
        }),
      )
      setClaimError(false)
      handleCloseModal()
    }
  }

  const handleRetry = async (values: FormikValues) => {
    const startDateObj = new Date(selectedStartDate)
    const finishDateObj = new Date(selectedFinishDate)
    const oneMonthFromStart = new Date(startDateObj)
    oneMonthFromStart.setMonth(oneMonthFromStart.getMonth() + 1)

    if (finishDateObj > oneMonthFromStart) {
      setValidateError('Длительность заявки не может превышать 1 месяц')
      waitTimer(() => setValidateError(''))
      return
    }

    const startDate = `${startDateString.split('.').reverse().join('-')} ${getFormatDate(
      timeStart,
    )}`
    const endDate = `${finishDateString.split('.').reverse().join('-')} ${getFormatDate(
      timeFinish,
    )}`

    const claimData = {
      token,
      userId: String(user.id),
      client: selectedClient,
      peopleCount: values.guestsNumber,
      placeId: id ? String(id) : String(platformId),
      startDate,
      endDate,
      force: true,
      status: 'inAnticipation',
      comment: values.comment || '',
    }

    const resultAction = await dispatch(createClaim(claimData))

    if (createClaim.rejected.match(resultAction)) {
      const errorMessage: any = resultAction.payload || resultAction.error.message

      if (errorMessage) {
        showError(errorMessage)
      }
    } else {
      showSuccess('Заявка успешно добавлена в лист ожидания')
      if (isWaitingList) {
        dispatch(getCurrentClaim({ claimId, token }))
        dispatch(getClaimComments({ requestId: claimId }))
        dispatch(
          getAllClaimsFromPlatforms({
            companyId: user?.employee?.company?.id
              ? String(user?.employee?.company?.id)
              : undefined,
            yearsAndMonth: yearMonthFormat ?? undefined,
            token,
          }),
        )
      }
      setClaimError(false)
      handleCloseModal()
    }
  }

  const openStartDateDropDown = (e: any) => {
    e.stopPropagation()
    setOpenStartDate(!openStartDate)
  }

  const openStartTimeDropDown = (e: any) => {
    e.stopPropagation()
    setOpenStartTime(!openStartTime)
  }

  const openFinishDateDropDown = (e: any) => {
    e.stopPropagation()
    setOpenFinishDate(!openFinishDate)
  }

  const openFinishTimeDropDown = (e: any) => {
    e.stopPropagation()
    setOpenFinishTime(!openFinishTime)
  }

  const handleTimeStartChange = (time: any) => {
    setTimeStart(time)
  }

  const handleTimeFinishChange = (time: any) => {
    setTimeFinish(time)
  }

  const dropdownStartDataRef = useRef<HTMLDivElement>(null)
  const dropdownStartTimeRef = useRef<HTMLDivElement>(null)
  const dropdownFinishDataRef = useRef<HTMLDivElement>(null)
  const dropdownFinishTimeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownStartDataRef.current &&
        !dropdownStartDataRef.current.contains(event.target as Node)
      ) {
        setOpenStartDate(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownFinishDataRef.current &&
        !dropdownFinishDataRef.current.contains(event.target as Node)
      ) {
        setOpenFinishDate(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownStartTimeRef.current &&
        !dropdownStartTimeRef.current.contains(event.target as Node)
      ) {
        setOpenStartTime(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownFinishTimeRef.current &&
        !dropdownFinishTimeRef.current.contains(event.target as Node)
      ) {
        setOpenFinishTime(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (id) {
      const foundItem = places.places.find((item) => item.id === id)
      if (foundItem) {
        setPlatformSize(foundItem?.size)
      }
    }
  }, [id])

  useEffect(() => {
    if (places?.places && places.places.length > 0) {
      const updatedPlatforms =
        places.places.map((item: any) => ({
          id: item.id,
          name: item.name,
        })) || []
      if (!platformName) {
        setPlatformNames(updatedPlatforms)
      } else {
        if (Array.isArray(platformName)) {
          setPlatformNames(
            platformName?.map((name) => ({
              id: 0,
              name,
            })),
          )
        } else {
          setPlatformNames([
            {
              id: 0,
              name: platformName,
            },
          ])
        }
      }
    } else if (!platformName && token && (!places?.places || places.places.length === 0)) {
      dispatch(
        getPlaces({
          token,
          page: 1,
          itemsPerPage: 1000,
        }),
      )
    }
  }, [platformName, places?.places, token, dispatch])

  useEffect(() => {
    if (!platformName && platformNames.length === 0 && places?.places && places.places.length > 0) {
      const updatedPlatforms = places.places.map((item: any) => ({
        id: item.id,
        name: item.name,
      }))
      setPlatformNames(updatedPlatforms)
    }
  }, [platformNames.length, places?.places, platformName])

  useEffect(() => {
    if (places?.places?.length > 0 && !platformName) {
      const defaultPlatform = places?.places[0]
      setPlatformId(defaultPlatform?.id)
      setPlatformSize(defaultPlatform?.size)
    }
    if (size) {
      setPlatformSize(size)
    }
  }, [places?.places, size])

  const clientOptions =
    client
      ?.filter((c) => c.type === clientType)
      .map((c) => ({
        value: c?.id,
        label: c?.organizationName || c?.fullName,
      })) || []

  const loadOptions = (inputValue: string, callback: (options: any[]) => void) => {
    const filtered = clientOptions?.filter((option) =>
      option?.label.toLowerCase().includes(inputValue.toLowerCase()),
    )
    callback(filtered)
  }

  return (
    <div className={styles.createClaim}>
      <div>
        <ModalHeader title='Создание заявки' onClickHandler={handleCloseModal} />
        <CreateClientModal
          isOpen={isCreateClient}
          onClose={() => setIsCreateClient(!isCreateClient)}
        />
        <CreateClientManagerModal
          isOpen={isCreateManager}
          onClose={() => setIsCreateManager(!isCreateManager)}
          clientId={Number(selectedClient)}
        />
      </div>
      <div className={styles.form}>
        <Formik
          initialValues={{
            // city: '',
            startDate: '',
            finishDate: '',
            startTime: '',
            finishTime: '',
            guestsNumber: '',
            platformName: platformNames?.length > 0 ? platformNames[0].name : '',
            customer,
            client,
            clientId,
            managerId,
            discountType,
            comment: '',
          }}
          validateOnBlur
          onSubmit={(values, actions) => handleSubmitHandler(values, actions)}
          validationSchema={validationSchema}
          enableReinitialize={false}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              isValid,
              handleChange,
              setFieldValue,
              handleBlur,
              handleSubmit,
              resetForm,
            } = props

            React.useEffect(() => {
              formikRef.current = resetForm
            }, [resetForm])

            return (
              <div className={styles.formContainer}>
                <div className={styles.inputGroup}>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Зал</h4>
                    {Array.isArray(platformNames) && !platformName ? (
                      <Select
                        name='platformName'
                        className={styles.selectInput}
                        classNamePrefix='react-select'
                        options={platformNames?.map((platform) => ({
                          value: platform.id.toString(),
                          label: platform.name,
                          id: platform.id,
                        }))}
                        value={
                          values.platformName
                            ? {
                                value: values.platformName,
                                label:
                                  platformNames?.find(
                                    (p) => p.id.toString() === values.platformName,
                                  )?.name || '',
                              }
                            : null
                        }
                        onChange={(selectedOption) => {
                          if (selectedOption) {
                            const selectedPlatformId = selectedOption.value
                            handleChange({
                              target: {
                                name: 'platformName',
                                value: selectedPlatformId,
                              },
                            })
                            setPlatformId(Number(selectedPlatformId))

                            const selectedPlatform = places?.places?.find(
                              (platform) => platform.id === Number(selectedPlatformId),
                            )

                            if (selectedPlatform) {
                              setPlatformSize(selectedPlatform.size)
                            }
                          } else {
                            handleChange({
                              target: {
                                name: 'platformName',
                                value: '',
                              },
                            })
                            setPlatformId(0)
                            setPlatformSize(0)
                          }
                        }}
                        onBlur={() => {
                          handleBlur({
                            target: {
                              name: 'platformName',
                            },
                          })
                        }}
                        styles={{
                          singleValue: (provided) => ({
                            ...provided,
                            color: values.platformName ? 'black' : provided.color,
                          }),
                          control: (provided) => ({
                            ...provided,
                            border: 'none',
                            boxShadow: 'none',
                            minHeight: 'auto',
                            backgroundColor: 'transparent',
                          }),
                          menu: (provided) => ({
                            ...provided,
                            right: 0,
                            overflow: 'auto',
                            height: '150px',
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            maxHeight: '150px',
                            overflowY: 'auto',
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: 0,
                            padding: 0,
                            height: isMobile ? 44 : 20,
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          indicatorSeparator: () => ({
                            display: 'none',
                          }),
                        }}
                        isClearable
                        placeholder='Выберите платформу'
                        noOptionsMessage={() => 'Нет доступных платформ'}
                      />
                    ) : (
                      <CustomInput
                        type='text'
                        placeholder=''
                        name='platformName'
                        readOnly
                        value={platformName ? platformName : platformNames[0]?.name || ''}
                        className={styles.input}
                      />
                    )}
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Менеджер</h4>
                    <CustomInput
                      type='text'
                      placeholder=''
                      name='customer'
                      readOnly
                      value={customer}
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Заказчик</h4>
                    <Select
                      options={[
                        { value: 'individual', label: 'Физ. лицо' },
                        { value: 'legal', label: 'Юр. лицо' },
                      ]}
                      value={
                        clientType
                          ? {
                              value: clientType,
                              label: clientType === 'individual' ? 'Физ. лицо' : 'Юр. лицо',
                            }
                          : null
                      }
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setClientType(selectedOption.value as 'individual' | 'legal')
                          setSelectedClient(undefined)
                          setFieldValue('clientId', '')
                        }
                      }}
                      className={styles.selectInput}
                      classNamePrefix='react-select'
                      isClearable={false}
                      placeholder='Выберите тип клиента'
                      styles={{
                        singleValue: (provided) => ({
                          ...provided,
                          color: clientType ? 'black' : provided.color,
                        }),
                        control: (provided) => ({
                          ...provided,
                          border: 'none',
                          boxShadow: 'none',
                          minHeight: 'auto',
                          backgroundColor: 'transparent',
                        }),
                        menu: (provided) => ({
                          ...provided,
                          right: 0,
                          overflow: 'auto',
                          height: 'auto',
                        }),
                        input: (provided) => ({
                          ...provided,
                          margin: 0,
                          padding: 0,
                          height: isMobile ? 44 : 20,
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                      }}
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Клиент</h4>
                    {client && client.length > 0 && (
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={clientOptions}
                        loadOptions={loadOptions}
                        onChange={(selectedOption) => {
                          setFieldValue('clientId', selectedOption?.value)
                          setSelectedClient(selectedOption?.value)
                        }}
                        onBlur={handleBlur}
                        value={
                          selectedClient
                            ? clientOptions.find(
                                (opt) =>
                                  opt?.value === values?.clientId || opt?.value === selectedClient,
                              )
                            : null
                        }
                        placeholder='Выберите клиента...'
                        isSearchable
                        className={styles.selectInput}
                        styles={{
                          singleValue: (provided) => ({
                            ...provided,
                            color: selectedClient ? 'black' : provided.color,
                          }),
                          control: (provided) => ({
                            ...provided,
                            border: 'none',
                            boxShadow: 'none',
                            minHeight: 'auto',
                            backgroundColor: 'transparent',
                          }),
                          menu: (provided) => ({
                            ...provided,
                            right: 0,
                            overflow: 'auto',
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            height: '100%',
                            maxHeight: '150px',
                            overflowY: 'auto',
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: 0,
                            padding: 0,
                            height: isMobile ? 44 : 20,
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          indicatorSeparator: () => ({
                            display: 'none',
                          }),
                        }}
                      />
                    )}
                  </div>
                  <div className={styles.createClientBlock}>
                    <button onClick={() => setIsCreateClient(!isCreateClient)}>
                      <img src={plusIcon} />
                      Создать Клиента
                    </button>
                  </div>
                  {/* {selectedClient && clientType == 'legal' && (
                    <>
                      <div className={styles.inputContainer}>
                        <h4 className={styles.subtitle}>Выберите менеджера</h4>
                        <Select
                          name='managerId'
                          required
                          options={[
                            { value: undefined, label: 'Без выбора менеджера' },
                            ...(clientManagers?.map((manager) => ({
                              value: manager?.id,
                              label: manager?.fullName,
                            })) || []),
                          ]}
                          value={
                            values.managerId === ''
                              ? null
                              : values.managerId === undefined
                              ? { value: undefined, label: 'Без выбора менеджера' }
                              : clientManagers?.find((m) => m?.id === values.managerId)
                              ? {
                                  value: values.managerId,
                                  label: clientManagers.find((m) => m?.id === values.managerId)
                                    ?.fullName,
                                }
                              : null
                          }
                          onChange={(selectedOption) => {
                            handleChange({
                              target: {
                                name: 'managerId',
                                value: selectedOption?.value,
                              },
                            })
                          }}
                          onBlur={() => {
                            handleBlur({
                              target: {
                                name: 'managerId',
                              },
                            })
                          }}
                          className={styles.selectInput}
                          classNamePrefix='react-select'
                          placeholder='Выберите менеджера'
                          noOptionsMessage={() => 'Нет доступных менеджеров'}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              border: 'none',
                              boxShadow: 'none',
                              minHeight: 'auto',
                              backgroundColor: 'transparent',
                            }),
                            menu: (provided) => ({
                              ...provided,
                              right: 0,
                              overflow: 'auto',
                              height: '70px',
                            }),
                            menuList: (provided) => ({
                              ...provided,
                              maxHeight: '150px',
                              overflowY: 'auto',
                            }),
                            input: (provided) => ({
                              ...provided,
                              margin: 0,
                              padding: 0,
                            }),
                            valueContainer: (provided) => ({
                              ...provided,
                              padding: 0,
                            }),
                            dropdownIndicator: (provided) => ({
                              ...provided,
                              padding: 0,
                            }),
                            clearIndicator: (provided) => ({
                              ...provided,
                              padding: 0,
                            }),
                            indicatorSeparator: () => ({
                              display: 'none',
                            }),
                          }}
                          isClearable={false}
                        />
                      </div>
                      <div className={styles.createClientBlock}>
                        <button onClick={() => setIsCreateManager(!isCreateManager)}>
                          <img src={plusIcon} />
                          Создать менеджера
                        </button>
                      </div>
                    </>
                  )} */}
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Дата и время начала</h4>
                    <div className={styles.containerTime}>
                      <div
                        onClick={(e) => openStartDateDropDown(e)}
                        style={
                          startDateString ? { color: 'black' } : { color: 'rgba(120, 120, 120, 1)' }
                        }
                        className={styles.dateValue}
                      >
                        {startDateString ? startDateString : 'Дата начала'}
                        <DropDown
                          ref={dropdownStartDataRef}
                          className={styles.dropDown}
                          open={openStartDate}
                        >
                          <Calendar
                            disableDayRestrictions={true}
                            selectedDate={selectedStartDate}
                            selectDate={(date) => {
                              setErr(null)
                              setIsRetrying(false)
                              onChangeStartDate(date)
                            }}
                          />
                        </DropDown>
                      </div>
                      <div className={styles.timeLeftBlock}>
                        <div className={styles.dateBlock} onClick={(e) => openStartTimeDropDown(e)}>
                          <div className={styles.dateBlock}>
                            <DatePicker
                              showTimeSelect
                              showTimeSelectOnly
                              selected={timeStart}
                              onChange={handleTimeStartChange}
                              timeFormat='HH:mm'
                              dateFormat='HH:mm'
                              timeIntervals={15}
                              timeCaption='Time'
                              customInput={customInput}
                              placeholderText='Время'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Дата и время окончания</h4>
                    <div className={styles.containerTime}>
                      <div
                        onClick={(e) => openFinishDateDropDown(e)}
                        style={
                          finishDateString
                            ? { color: 'black' }
                            : { color: 'rgba(120, 120, 120, 1)' }
                        }
                        className={styles.dateValue}
                      >
                        {finishDateString ? finishDateString : 'Дата окончания'}
                        <DropDown
                          ref={dropdownFinishDataRef}
                          className={styles.dropDown}
                          open={openFinishDate}
                        >
                          <Calendar
                            disableDayRestrictions={true}
                            selectedDate={selectedFinishDate}
                            selectDate={(date) => {
                              setErr(null)
                              setIsRetrying(false)
                              onChangeFinishDate(date)
                            }}
                          />
                        </DropDown>
                      </div>
                      <div className={styles.timeRightBlock}>
                        <div
                          className={styles.dateBlock}
                          onClick={(e) => openFinishTimeDropDown(e)}
                        >
                          <div className={styles.dateBlock}>
                            <DatePicker
                              showTimeSelect
                              showTimeSelectOnly
                              selected={timeFinish}
                              onChange={handleTimeFinishChange}
                              timeFormat='HH:mm'
                              dateFormat='HH:mm'
                              timeIntervals={15}
                              timeCaption='Time'
                              customInput={customInput}
                              placeholderText='Время'
                            />
                          </div>
                        </div>
                      </div>{' '}
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Количество гостей</h4>
                    <CustomInput
                      style={{ width: isMobile ? '100%' : '130px' }}
                      type='text'
                      placeholder='Кол-во гостей'
                      name='guestsNumber'
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.guestsNumber}
                      className={
                        errors.guestsNumber && touched.guestsNumber
                          ? `${styles.inputError} ${styles.input}`
                          : styles.input
                      }
                    />
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Комментарий</h4>
                    <CustomTextArea
                      value={values.comment}
                      callbackHandler={(e) => {
                        setFieldValue('comment', e.target.value)
                      }}
                      name='comment'
                      placeholder='Введите комментарий'
                      withoutFile
                      className={styles.textarea}
                    />
                  </div>
                  {touched.guestsNumber && errors.guestsNumber ? (
                    <p style={{ marginBottom: 10 }} className={s.errorValidation}>
                      {String(errors.guestsNumber)}
                    </p>
                  ) : null}

                  {validateError ? (
                    <p
                      style={{ marginBottom: 10 }}
                      className={`${s.errorValidation} ${s.networkError}`}
                    >
                      {String(validateError)}
                    </p>
                  ) : null}
                  {error ? (
                    <p
                      style={{ marginBottom: 10 }}
                      className={`${s.errorValidation} ${s.networkError}`}
                    >
                      {String(error)}
                    </p>
                  ) : null}
                  {err ? (
                    <p
                      style={{ marginBottom: 10 }}
                      className={`${s.errorValidation} ${s.networkError}`}
                    >
                      {err as string}
                    </p>
                  ) : null}
                </div>
                <div className={styles.footerContainer}>
                  <CustomButton
                    title={isRetrying ? 'Добавить в лист ожидания' : 'Сохранить'}
                    disabled={!isValid}
                    onClick={isRetrying ? () => handleRetry(values) : () => handleSubmit()}
                    type='submit'
                    className={styles.submitBtn}
                  />
                </div>
              </div>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default CreateClaim
